import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * Aurora PageContainer — the canonical width / padding shell for every
 * routed page that lives under `<AppShell>`. Replaces the scattered
 * `max-w-7xl space-y-6 p-6` boilerplate.
 *
 * - Caps width at `max-w-screen-2xl` (1536px) so content stays readable on
 *   ultra-wide monitors but documents can still breathe on a 13" laptop.
 * - Pads 16px (mobile) → 24px (sm) → 32px (lg) — matches the rule in the
 *   design-system README.
 * - Defaults to `as="main"` so a route component that wants to *be* the
 *   page landmark can do so in one tag.
 *
 * ```tsx
 * <PageContainer>
 *   <PageHeader title="Documents" />
 *   <PageBody>{…}</PageBody>
 * </PageContainer>
 * ```
 */
type PageContainerProps = React.ComponentProps<'div'> & {
  /**
   * Tighter widths for content-dense pages.
   * - `default` — 1536px (max-w-screen-2xl)
   * - `narrow`  — 880px (article-style routes like Account, Settings)
   * - `wide`    — full width (workspace tools that need every pixel)
   */
  width?: 'default' | 'narrow' | 'wide'
}

function PageContainer({
  className,
  width = 'default',
  ...props
}: PageContainerProps) {
  return (
    <div
      data-slot="page-container"
      data-width={width}
      className={cn(
        'mx-auto w-full px-4 py-6 sm:px-6 lg:px-8 lg:py-8',
        width === 'default' && 'max-w-screen-2xl',
        width === 'narrow' && 'max-w-3xl',
        width === 'wide' && 'max-w-none',
        className,
      )}
      {...props}
    />
  )
}

/**
 * Vertical rhythm wrapper for the body of a page — keeps the gap between
 * a PageHeader and the rest of the content uniform across routes. Use
 * `gap="loose"` when sections are visually distinct cards; `gap="tight"`
 * for dense forms.
 */
type PageBodyProps = React.ComponentProps<'div'> & {
  gap?: 'tight' | 'default' | 'loose'
}

function PageBody({ className, gap = 'default', ...props }: PageBodyProps) {
  return (
    <div
      data-slot="page-body"
      data-gap={gap}
      className={cn(
        'flex flex-col',
        gap === 'tight' && 'gap-4 pt-4 sm:pt-6',
        gap === 'default' && 'gap-6 pt-6',
        gap === 'loose' && 'gap-8 pt-8',
        className,
      )}
      {...props}
    />
  )
}

/**
 * Group children into a "section": optional SectionHeader + body. Lives
 * here rather than in `page-header.tsx` so consumers can `import { Section }`
 * alongside PageContainer without pulling in the header types.
 */
function Section({
  className,
  ...props
}: React.ComponentProps<'section'>) {
  return (
    <section
      data-slot="section"
      className={cn('flex flex-col gap-3', className)}
      {...props}
    />
  )
}

export { PageContainer, PageBody, Section }
